import React, { createContext, useContext, useReducer } from 'react';
import { Alert, AlertType } from '../types';

interface AlertState {
  alerts: Alert[];
}

type AlertAction = 
  | { type: 'ADD_ALERT'; payload: Omit<Alert, 'id' | 'timestamp'> }
  | { type: 'REMOVE_ALERT'; payload: string };

const initialState: AlertState = {
  alerts: []
};

const alertReducer = (state: AlertState, action: AlertAction): AlertState => {
  switch (action.type) {
    case 'ADD_ALERT':
      const newAlert: Alert = {
        ...action.payload,
        id: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now()
      };
      return {
        ...state,
        alerts: [newAlert, ...state.alerts.slice(0, 4)] // Keep max 5 alerts
      };
    case 'REMOVE_ALERT':
      return {
        ...state,
        alerts: state.alerts.filter(alert => alert.id !== action.payload)
      };
    default:
      return state;
  }
};

interface AlertContextType {
  alerts: Alert[];
  showAlert: (message: string, type: AlertType) => void;
  removeAlert: (id: string) => void;
}

const AlertContext = createContext<AlertContextType | null>(null);

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(alertReducer, initialState);

  const showAlert = (message: string, type: AlertType) => {
    dispatch({ type: 'ADD_ALERT', payload: { message, type } });
  };

  const removeAlert = (id: string) => {
    dispatch({ type: 'REMOVE_ALERT', payload: id });
  };

  return (
    <AlertContext.Provider value={{ alerts: state.alerts, showAlert, removeAlert }}>
      {children}
    </AlertContext.Provider>
  );
};

export const useAlertContext = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlertContext must be used within AlertProvider');
  }
  return context;
};